<template>
  <div class="bDelivery">
    <v-progress-circular v-if="loadingDeliveries" />
    <template v-else>
      <div
        v-if="buyoutDeliveryErrors?.delivery_type_identifier"
        class="app__label color--redTone2"
      >
        {{ (buyoutDeliveryErrors.delivery_type_identifier as string[])[0] }}
      </div>
      <div
        v-for="deliveryType in deliveryTypes"
        id="deliveryTypeSelector"
        :key="deliveryType.identifier"
        class="bDelivery__item cursor-pointer"
        :class="{ 'is-selected': selectedDeliveryType === deliveryType }"
        @click="selectedDeliveryType = deliveryType; emit('scrollToBankInfo');"
      >
        <img :src="deliveryType.image" :alt="deliveryType.title">
        <div class="app__title app__title--h6 font-poppins color--violetTone1">
          {{ deliveryType.title }}
        </div>
        <div class="bDelivery__info font-medium">
          <span v-if="deliveryType.distance" class="bDelivery__distance text-no-wrap">
            <v-icon icon="ct:pin" size="x-small" />
            {{ roundDistance(deliveryType.distance) }}
          </span>
          <span v-if="!deliveryType.is_later" class="color--greenTone1">{{ t("buyout.appraisal.for_free") }}</span>
        </div>
        <div class="bDelivery__desc color--grayTone3">
          {{ deliveryType.description }}
        </div>
      </div>
    </template>
  </div>
  <div
    v-if="get(buyoutDeliveryErrors, 'residence_country')"
    class="app__label color--redTone2 mt-3"
  >
    {{ (buyoutDeliveryErrors.residence_country as string[])[0] }}
  </div>
  <v-form
    v-if="selectedDeliveryType?.is_courier"
    v-model="pickUpAddressFormValid"
    class="app__form"
  >
    <div
      id="pickUpAddress"
      class="formItem formItem--1x12 formItem--checkbox formItem--noLabel"
    >
      <v-checkbox v-model="showOtherPickupAddress" :label="t('buyout.appraisal.pickup_address_not_same_as_permanent_address')" />
    </div>
    <template v-if="showOtherPickupAddress">
      <common-address-form
        v-model="otherPickupAddress"
        :error-messages="buyoutDeliveryErrors.pickup_address"
        :is-pickup-address="true"
        @error-reset="resetErrorMessage"
      />
      <dialog-address-validation
        v-if="addressValidationError !== null"
        :validation-result="addressValidationError"
        :original-address="pickUpAddress"
        persistent
        @cancel="addressValidationError = null"
        @continue="setAddressAndTryToContinue(null)"
        @continue-with-address="setAddressAndTryToContinue"
      />
    </template>
  </v-form>
  <template v-if="selectedDeliveryType?.is_courier">
    <div class="app__title app__title--h4 mb-3">
      {{ t("buyout.appraisal.select_pickup_date") }}
    </div>
    <v-slide-group
      id="deliveryDate"
      v-model="pickupDate"
      class="mt-4"
      :center-active="true"
      :show-arrows="true"
      @update:model-value="delete buyoutDeliveryErrors.delivery_date"
    >
      <v-slide-group-item
        v-for="(deliveryDate, index) in deliverySlots"
        :key="index"
        v-slot="{ isSelected, toggle }"
        :value="deliveryDate"
      >
        <v-btn
          :disabled="loadingDeliveries"
          :loading="loadingDeliveries"
          size="large"
          :class="[isSelected ? 'mbtn--violetAltInv' : 'mbtn--trans', 'mr-3']"
          @click="toggle"
        >
          {{ d(new Date(deliveryDate)) }}
        </v-btn>
      </v-slide-group-item>
    </v-slide-group>
    <v-alert
      v-if="buyoutDeliveryErrors?.delivery_date"
      class="vAlert--errorAlt vAlert--noBorder mt-4"
    >
      <template #prepend>
        <v-icon icon="ct:circle-exclamation" size="x-small" />
      </template>
      <div class="desc color--redTone2">
        {{ (buyoutDeliveryErrors?.delivery_date as string[])[0] }}
      </div>
    </v-alert>
  </template>
</template>

<script setup lang="ts">
import { get, isEmpty } from "lodash-es";
import { storeToRefs } from "pinia";
import type { AddressValidationResult } from "cashtec-common/src/components/dialog/AddressValidation.vue";

const { t, d } = useI18n();
const { notify } = useNotification();
const buyoutStore = useBuyoutStore();
const bonusPartnerWithManufacturerBuyoutStore = useBonusPartnerWithManufacturerBuyoutStore();
const authStore = useAuthStore();
const api = useApi();
const { roundDistance } = useUtils();
const { $api } = useNuxtApp() as { $api: IApiInstance };

const {
  buyoutPersonalData,
  buyoutPersonalDataErrors,
  pickUpAddress,
  mainBuyoutStep,
  userBuyoutCode,
  selectedDeliveryType,
} = storeToRefs(buyoutStore);

const {
  buyoutFinished,
  buyoutFinishedByHandover
} = storeToRefs(bonusPartnerWithManufacturerBuyoutStore);

const props = withDefaults(defineProps<{
  orderDeliveryLaterAvailable?: boolean,
  isBuyoutDetail?: boolean
}>(), {
  orderDeliveryLaterAvailable: false,
  isBuyoutDetail: false
});

const showOtherPickupAddress = ref<boolean>(false);
const pickUpAddressFormValid = ref<boolean>(false);
const moveToThankYouLoading = ref<boolean>(false);
const deliveryTypes = ref<DeliveryType[]>([]);
const loadingDeliveries = ref<boolean>(false);
const deliverySlots = ref<string[]>([]);
const pickupDate = ref<any>(null);
const otherPickupAddress = ref<PickUpAddress>(pickUpAddress.value);
const buyoutDeliveryErrors = ref<
    {
      pickup_address?: {
        street_and_number?: string | string[],
        postal_code?: string | string[],
        city?: string | string[],
        country_of_birth?: string | string[]
      },
      delivery_type_identifier?: string | string[],
      residence_country?: string | string[],
      delivery_date?: string | string[],
    }
>({});
const addressValidationError = ref<AddressValidationResult|null>(null);
const wasAddressConfirmed = ref<boolean>(false);

defineExpose({ moveToThankYou, moveToThankYouLoading });
const emit = defineEmits(["deliveryOrdered", "scrollToBankInfo"]);

onMounted(() => {
  if (authStore.isPartner && !authStore.isPartnerBrandStore) {
    navigateTo(useLocalePath()({ name: "index" }));
  }
  getAvailableDeliveries();
  if (authStore.isLoggedIn && !authStore.isPartnerBrandStore) {
    getUserAdditionalData();
  }
  navigator.geolocation?.getCurrentPosition(({ coords }) => getAvailableDeliveries(false, coords));
});

async function getAvailableDeliveries (setLoadingStatus: boolean = true, coords?: GeolocationCoordinates) {
  loadingDeliveries.value = setLoadingStatus;

  const coordsString = (coords !== undefined) ? `?lat=${coords.latitude}&lon=${coords.longitude}` : "";
  const { data } = await api.call<DeliveryType[]>(`${api.commonUrls.delivery.AVAILABLE_DELIVERY_TYPES(userBuyoutCode.value ?? "")}${coordsString}`);
  if (data.value) {
    deliveryTypes.value = data.value;
    if (props.orderDeliveryLaterAvailable) {
      deliveryTypes.value.push(
        {
          identifier: "order_transport_later",
          title: t("buyout.delivery.order_later_title"),
          description: t("buyout.delivery.order_later_description"),
          image: "/media/delivery_type_images/calendar-time.svg",
          is_courier: false,
          is_later: true,
          distance: null
        });
    }
    if (selectedDeliveryType.value !== null) {
      // try to keep the previously selected delivery type when updating
      selectedDeliveryType.value =
        deliveryTypes.value.find(it => it.identifier === selectedDeliveryType?.value?.identifier) ?? null;
    }
  }
  loadingDeliveries.value = false;
}

async function getUserAdditionalData () {
  // TO DO - load only pickup_address
  const { data, error } = await api.call<PersonalDataInfo>(api.commonUrls.user.USER_ADDITIONAL_DATA());
  if (error.value) {
    notify({
      title: t("form.common.error"),
      text: error.value!.data?.detail ?? t("general.error_sending_request"),
      type: "error"
    });
  }
  if (data.value.pickup_address) {
    pickUpAddress.value = data.value.pickup_address;
    if (pickUpAddress?.value?.street_and_number || pickUpAddress?.value?.city || pickUpAddress?.value?.postal_code) {
      otherPickupAddress.value = pickUpAddress.value;
      showOtherPickupAddress.value = true;
    }
  }
}

async function getDeliveryTypeSlots () {
  if (selectedDeliveryType.value) {
    const { data } = await api.call<string[]>(api.commonUrls.delivery.DELIVERY_TYPE_SLOTS(selectedDeliveryType.value.identifier));
    if (data.value) {
      deliverySlots.value = data.value;
    }
  }
}

async function moveToThankYou () {
  moveToThankYouLoading.value = true;
  if (!props.isBuyoutDetail) {
    await updateCustomerInfo();
  }
  if (isEmpty(buyoutPersonalDataErrors.value)) {
    await orderDelivery();
  }
  moveToThankYouLoading.value = false;
}

async function updateCustomerInfo () {
  buyoutPersonalDataErrors.value = {};
  interface Payload {
    registration_number: string | null;
    bank_account_number: string;
    bank_code: string;
    iban: string;
  }

  const payload: Payload = {
    registration_number: userBuyoutCode.value,
    bank_account_number: buyoutPersonalData!.value.bank_account_number,
    bank_code: buyoutPersonalData!.value.bank_code,
    iban: buyoutPersonalData!.value.iban
  };


  //TMP hack to make David happi
  if(selectedDeliveryType.value && selectedDeliveryType.value?.identifier==="cashtec_brand_store_handover"){
    payload.bank_account_number = "000000000";
    payload.bank_code = "0300";
  }

  const { error } = await api.call<string>(api.commonUrls.buyout.UPDATE_CUSTOMER_INFO(), {
    method: "POST",
    body: { ...payload }
  });
  if (error.value) {
    notify({
      title: t("form.common.error"),
      text: t("general.error_sending_request"),
      type: "error"
    });
    buyoutPersonalDataErrors.value = error.value?.data;
  }
}

async function orderDelivery () {
  if (selectedDeliveryType.value?.is_later) {
    const { data, error } = await api.call<string | Record<string, string[]>>(api.commonUrls.delivery.ORDER_DELIVERY_LATER(), {
      method: "POST",
      body: { user_buyout_code: userBuyoutCode.value }
    });
    if (error.value) {
      notify({
        title: t("form.common.error"),
        text: t("general.error_sending_request"),
        type: "error"
      });
      buyoutDeliveryErrors.value = error.value?.data;
    }
    if (data.value) {
      buyoutFinished.value = true;
      mainBuyoutStep.value += 1;
    }
    return;
  }

  if (
    selectedDeliveryType.value &&
  ["cashtec_brand_store_handover", "cashtec_samsung_service_store_handover"].includes(selectedDeliveryType.value.identifier)
  ) {

    const apiCall =
      selectedDeliveryType.value.identifier === "cashtec_brand_store_handover"
        ? $api.delivery.handleBuyoutOnBrandStore
        : $api.delivery.handleBuyoutOnServiceStore;

    const {data, error} = await apiCall.bind($api.delivery)(userBuyoutCode.value);

    if (error.value) {
      notify({
        title: t("form.common.error"),
        text: t("general.error_sending_request"),
        type: "error"
      });
      buyoutDeliveryErrors.value = error.value?.data;
    }
    if (data.value) {
      buyoutFinished.value = true;
      buyoutFinishedByHandover.value = true;
      if(selectedDeliveryType.value.identifier==="cashtec_brand_store_handover"){
        await bonusPartnerWithManufacturerBuyoutStore.printContactPdf();
      }
      mainBuyoutStep.value += 1;
    }
    return;
  }

  const payload = {
    delivery_type_identifier: selectedDeliveryType.value?.identifier,
    buyout_registration_number: userBuyoutCode.value,
    ...(
      selectedDeliveryType.value?.is_courier
        ? {
          delivery_date: pickupDate.value,
          ...(showOtherPickupAddress.value
            ? {
              pickup_address: {
                ...otherPickupAddress.value,
                country: otherPickupAddress!.value!.country!.id,
                was_address_confirmed: wasAddressConfirmed.value
              }
            }
            : {})
        }
        : {}
    )
  };

  const { error } = await api.call<string | Record<string, string[]>>(api.commonUrls.delivery.ORDER_DELIVERY(), {
    method: "POST",
    body: { ...payload }
  });
  if (error.value) {
    if (error.value?.data){
      buyoutDeliveryErrors.value = error.value.data;
      if ("delivery_type_identifier" in buyoutDeliveryErrors.value) {
        useGoTo("#deliveryTypeSelector", {offset: -200});
      }
      if ("delivery_date" in buyoutDeliveryErrors.value) {
        useGoTo("#deliveryDate", {offset: -200});
      }
      if ("pickup_address" in buyoutDeliveryErrors.value || "residence_country" in buyoutDeliveryErrors.value) {
        showOtherPickupAddress.value = true;
        useGoTo("#pickUpAddress", {offset: -200});
      }
      if ("country" in buyoutDeliveryErrors.value) {
        useGoTo("#pickUpAddress", {offset: -200});
      }
      if (Object.prototype.hasOwnProperty.call(error.value?.data, "x_address")) {
        addressValidationError.value = error.value!.data.x_address;
        // @ts-ignore
        delete buyoutDeliveryErrors.value.x_address;
      }
    }
    else {
      notify({
        title: t("form.common.error"),
        text: t("general.error_sending_request"),
        type: "error"
      });
    }
  } else {
    buyoutFinished.value = true;
    mainBuyoutStep.value += 1;
    emit("deliveryOrdered");
  }
}

function setAddressAndTryToContinue (newAddress: {street_and_number:string, postal_code:string, city:string}|null) {
  if (newAddress !== null) {
    otherPickupAddress.value.street_and_number = newAddress.street_and_number;
    otherPickupAddress.value.postal_code = newAddress.postal_code;
    otherPickupAddress.value.city = newAddress.city;
  } else {
    // set the address to be absolutely correct only when the user explicitly confirms it,
    // otherwise validate it once more to be sure
    wasAddressConfirmed.value = true;
  }

  addressValidationError.value = null;
}

function resetErrorMessage (key: string) {
  // @ts-ignore
  delete buyoutDeliveryErrors.value[key];
}

watch(selectedDeliveryType, () => {
  resetErrorMessage("delivery_type_identifier");
  resetErrorMessage("residence_country");
  if (selectedDeliveryType.value?.is_courier) {
    getDeliveryTypeSlots();
  }
});
</script>
